import { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

interface NavbarProps {
  lang?: 'zh' | 'en';
  onLangToggle?: () => void;
}

const navLinks = [
  { label: { zh: '关于我们', en: 'About' }, href: '/about', anchor: false, icon: 'ri-planet-line', color: '#00FF88', bg: 'rgba(0,255,136,0.15)' },
  { label: { zh: '元宇宙愿景', en: 'Vision' }, href: '#meta-vision', anchor: true, icon: 'ri-earth-line', color: '#00FFCC', bg: 'rgba(0,255,204,0.15)' },
  { label: { zh: '核心技术', en: 'CoreTech' }, href: '/tech', anchor: false, icon: 'ri-cpu-line', color: '#FFD700', bg: 'rgba(255,215,0,0.15)' },
  { label: { zh: 'Web演进', en: 'Network' }, href: '/network', anchor: false, icon: 'ri-git-branch-line', color: '#00FF88', bg: 'rgba(0,255,136,0.15)' },
  { label: { zh: 'XR体验', en: 'XR' }, href: '/xr', anchor: false, icon: 'ri-glasses-2-line', color: '#FF6B9D', bg: 'rgba(255,107,157,0.15)' },
  { label: { zh: '火星城市', en: 'Mars' }, href: '/mars', anchor: false, icon: 'ri-rocket-line', color: '#FF6B35', bg: 'rgba(255,107,53,0.15)' },
  { label: { zh: '前沿科技', en: 'Future' }, href: '/future-tech', anchor: false, icon: 'ri-brain-line', color: '#A855F7', bg: 'rgba(168,85,247,0.15)' },
  { label: { zh: 'NEXUS-1', en: 'NEXUS' }, href: '/nexus', anchor: false, icon: 'ri-robot-line', color: '#00FFCC', bg: 'rgba(0,255,204,0.15)' },
  { label: { zh: '人物志', en: 'Pioneers' }, href: '#meta-pioneers', anchor: true, icon: 'ri-user-star-line', color: '#FF6B9D', bg: 'rgba(255,107,157,0.15)' },
  { label: { zh: '资讯', en: 'News' }, href: '/news', anchor: false, icon: 'ri-article-line', color: '#FFD700', bg: 'rgba(255,215,0,0.15)' },
  { label: { zh: '商城', en: 'Shop' }, href: '/shop', anchor: false, icon: 'ri-store-3-line', color: '#FFD700', bg: 'rgba(255,215,0,0.15)' },
  { label: { zh: '星际商城', en: 'OMNI MALL' }, href: '/omniverse-mall', anchor: false, icon: 'ri-store-2-line', color: '#FF6B35', bg: 'rgba(255,107,53,0.15)' },
];

export default function Navbar({ lang = 'zh', onLangToggle }: NavbarProps) {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('');
  const [litKey, setLitKey] = useState(0);
  const litIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    litIntervalRef.current = setInterval(() => {
      setLitKey((k) => (k + 1) % navLinks.length);
    }, 650);
    return () => {
      if (litIntervalRef.current) clearInterval(litIntervalRef.current);
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNav = (e: React.MouseEvent<HTMLAnchorElement>, link: { href: string; anchor: boolean }) => {
    if (!link.anchor) return;
    e.preventDefault();
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        const el = document.querySelector(link.href);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 300);
    } else {
      const el = document.querySelector(link.href);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }
    setActiveSection(link.href);
    setMenuOpen(false);
  };

  return (
    <nav
      className="fixed top-0 left-0 w-full z-50 transition-all duration-500"
      style={scrolled ? {
        background: 'rgba(2,6,18,0.96)',
        borderBottom: '1px solid rgba(0,255,200,0.1)',
        backdropFilter: 'blur(28px)',
      } : { background: 'transparent' }}
    >
      {/* Top rainbow accent line */}
      <div
        className="absolute top-0 left-0 w-full h-0.5"
        style={{
          background: 'linear-gradient(90deg, #00FF88, #FFD700, #FF6B9D, #FF6B35, #00FFCC, #A855F7, #00FF88)',
          opacity: scrolled ? 0.8 : 0.35,
          transition: 'opacity 0.5s',
        }}
      />

      <div className="max-w-[1500px] mx-auto px-4 md:px-8 flex items-center justify-between h-16 md:h-[70px]">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-3 group flex-shrink-0">
          <div className="relative w-10 h-10 flex items-center justify-center flex-shrink-0">
            <div
              className="absolute inset-0 transition-all duration-300 group-hover:scale-110"
              style={{
                border: '2px solid rgba(0,255,200,0.7)',
                borderRadius: '50% 40% 60% 45% / 45% 60% 40% 55%',
                background: 'rgba(0,255,200,0.12)',
                boxShadow: '0 0 20px rgba(0,255,200,0.25)',
              }}
            />
            <span className="relative font-black text-xl" style={{ fontFamily: 'Orbitron, sans-serif', color: '#00FFCC' }}>M</span>
          </div>
          <div className="flex flex-col leading-none">
            <span className="font-black text-xl tracking-widest text-white" style={{ fontFamily: 'Orbitron, sans-serif', letterSpacing: '0.18em' }}>
              META<span style={{ color: '#00FFCC' }}>66</span>
            </span>
            <span className="text-xs tracking-widest" style={{ color: 'rgba(0,255,200,0.4)', fontFamily: 'Caveat, cursive', fontSize: '0.62rem', letterSpacing: '0.22em' }}>
              METAVERSE NEXUS
            </span>
          </div>
        </Link>

        {/* Desktop Nav - Rainbow Piano Keys with BG */}
        <div className="hidden xl:flex items-center gap-1">
          {navLinks.map((link, idx) => {
            const isLit = litKey === idx;
            const isActive = activeSection === link.href || (!link.anchor && location.pathname.startsWith(link.href));

            const innerContent = (
              <>
                {/* Background fill when lit */}
                <div
                  className="absolute inset-0 transition-all duration-300"
                  style={{
                    background: isLit ? link.bg : isActive ? `${link.color}08` : 'transparent',
                    borderRadius: '9px 7px 11px 9px / 7px 11px 9px 7px',
                    border: isLit ? `1.5px solid ${link.color}70` : isActive ? `1px solid ${link.color}25` : '1px solid transparent',
                    boxShadow: isLit ? `0 0 18px ${link.color}35, 0 2px 12px ${link.color}20` : 'none',
                  }}
                />
                <i
                  className={`${link.icon} text-sm relative z-10 transition-all duration-300`}
                  style={{ color: isLit ? link.color : isActive ? link.color : 'rgba(255,255,255,0.35)' }}
                />
                <span
                  className="relative z-10 transition-all duration-300"
                  style={{
                    color: isLit ? link.color : isActive ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.5)',
                    textShadow: isLit ? `0 0 10px ${link.color}70` : 'none',
                    fontFamily: isLit ? 'Caveat, cursive' : 'inherit',
                    fontSize: isLit ? '0.95rem' : '0.8rem',
                    fontWeight: isLit ? 700 : 500,
                  }}
                >
                  {link.label[lang]}
                </span>
                {/* Bottom glow bar */}
                <span
                  className="absolute bottom-0.5 left-2 right-2 h-0.5 rounded-full transition-all duration-300"
                  style={{
                    background: link.color,
                    opacity: isLit ? 1 : isActive ? 0.4 : 0,
                    transform: isLit ? 'scaleX(1)' : isActive ? 'scaleX(0.5)' : 'scaleX(0)',
                    boxShadow: isLit ? `0 0 6px ${link.color}` : 'none',
                  }}
                />
              </>
            );

            if (link.anchor) {
              return (
                <div key={link.href} className="relative">
                  <a
                    href={link.href}
                    onClick={(e) => handleNav(e, link)}
                    className="relative flex items-center gap-1.5 px-3 py-2 cursor-pointer whitespace-nowrap rounded-xl"
                    style={{ minWidth: '72px', justifyContent: 'center' }}
                  >
                    {innerContent}
                  </a>
                </div>
              );
            }

            return (
              <div key={link.href} className="relative">
                <Link
                  to={link.href}
                  className="relative flex items-center gap-1.5 px-3 py-2 whitespace-nowrap rounded-xl"
                  style={{ minWidth: '72px', justifyContent: 'center' }}
                >
                  {innerContent}
                </Link>
              </div>
            );
          })}
        </div>

        {/* Right Actions */}
        <div className="hidden md:flex items-center gap-2">
          <a
            href="https://www.rwa99.cn"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold transition-all duration-300 whitespace-nowrap hover:scale-105 cursor-pointer"
            style={{
              background: 'rgba(255,180,0,0.12)',
              border: '1.5px solid rgba(255,180,0,0.45)',
              borderRadius: '50% 45% 55% 50% / 50% 55% 45% 50%',
              color: 'rgba(255,200,50,0.95)',
              fontFamily: 'Caveat, cursive',
              fontSize: '0.88rem',
              boxShadow: '0 0 14px rgba(255,180,0,0.18)',
            }}
          >
            <i className="ri-links-line text-xs" />
            RWA99
          </a>

          <button
            onClick={onLangToggle}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold transition-all duration-200 cursor-pointer whitespace-nowrap hover:scale-105"
            style={{
              border: '1px solid rgba(255,255,255,0.15)',
              borderRadius: '50% 45% 55% 50% / 50% 55% 45% 50%',
              color: 'rgba(255,255,255,0.5)',
              background: 'rgba(255,255,255,0.04)',
            }}
          >
            <i className="ri-translate-2 text-xs" />
            <span>{lang === 'zh' ? 'EN' : '中'}</span>
          </button>

          <Link
            to="/contact"
            className="px-4 py-2 text-sm font-semibold transition-all duration-300 whitespace-nowrap hover:scale-105"
            style={{
              border: '1px solid rgba(255,255,255,0.18)',
              borderRadius: '8px 6px 10px 8px / 6px 10px 8px 6px',
              color: 'rgba(255,255,255,0.6)',
              background: 'rgba(255,255,255,0.04)',
            }}
          >
            {lang === 'zh' ? '联系我们' : 'Contact'}
          </Link>

          <Link
            to="/contact?tab=demo"
            className="px-5 py-2 text-sm font-black transition-all duration-300 whitespace-nowrap hover:scale-105"
            style={{
              background: 'linear-gradient(135deg, rgba(0,255,200,0.92), rgba(0,200,255,0.92))',
              borderRadius: '8px 6px 10px 8px / 6px 10px 8px 6px',
              color: '#020612',
              fontFamily: 'Orbitron, sans-serif',
              fontSize: '0.72rem',
              letterSpacing: '0.05em',
              boxShadow: '0 0 22px rgba(0,255,200,0.32)',
            }}
          >
            GET DEMO
          </Link>
        </div>

        {/* Mobile Menu Button */}
        <button
          className="xl:hidden w-9 h-9 flex items-center justify-center cursor-pointer transition-all duration-200"
          style={{
            border: '1px solid rgba(0,255,200,0.28)',
            borderRadius: '8px 6px 10px 8px / 6px 10px 8px 6px',
            background: 'rgba(0,255,200,0.07)',
            color: 'rgba(0,255,200,0.85)',
          }}
          onClick={() => setMenuOpen(!menuOpen)}
        >
          <i className={menuOpen ? 'ri-close-line text-xl' : 'ri-menu-3-line text-xl'} />
        </button>
      </div>

      {/* Mobile Menu */}
      <div className={`xl:hidden overflow-hidden transition-all duration-400 ease-in-out ${menuOpen ? 'max-h-[900px] opacity-100' : 'max-h-0 opacity-0'}`}>
        <div
          className="border-t px-6 py-5 flex flex-col gap-1"
          style={{ background: 'rgba(2,6,18,0.98)', borderTopColor: 'rgba(0,255,200,0.1)', backdropFilter: 'blur(24px)' }}
        >
          {navLinks.map((link, idx) =>
            link.anchor ? (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => handleNav(e, link)}
                className="flex items-center gap-3 text-white/55 hover:text-white text-sm font-medium py-3 px-4 transition-all duration-200 cursor-pointer rounded-xl"
                style={{ transitionDelay: `${idx * 25}ms`, background: 'rgba(255,255,255,0.02)' }}
              >
                <i className={`${link.icon} text-sm`} style={{ color: link.color }} />
                {link.label[lang]}
              </a>
            ) : (
              <Link
                key={link.href}
                to={link.href}
                onClick={() => setMenuOpen(false)}
                className="flex items-center gap-3 text-white/55 hover:text-white text-sm font-medium py-3 px-4 transition-all duration-200 rounded-xl"
                style={{ transitionDelay: `${idx * 25}ms`, background: 'rgba(255,255,255,0.02)' }}
              >
                <i className={`${link.icon} text-sm`} style={{ color: link.color }} />
                {link.label[lang]}
              </Link>
            )
          )}
          <div className="flex flex-col gap-3 mt-4 pt-4" style={{ borderTop: '1px solid rgba(0,255,200,0.08)' }}>
            <a
              href="https://www.rwa99.cn"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 py-3 px-5 text-sm font-bold cursor-pointer rounded-xl"
              style={{ background: 'rgba(255,180,0,0.08)', border: '1px solid rgba(255,180,0,0.25)', color: 'rgba(255,200,50,0.85)', fontFamily: 'Caveat, cursive' }}
            >
              <i className="ri-links-line" />
              访问 RWA99.cn — RWA资产生态平台
            </a>
            <Link
              to="/contact?tab=demo"
              onClick={() => setMenuOpen(false)}
              className="flex items-center justify-center gap-2 py-3 px-5 text-sm font-black rounded-xl"
              style={{ background: 'linear-gradient(135deg, rgba(0,255,200,0.9), rgba(0,200,255,0.9))', color: '#020612', fontFamily: 'Orbitron, sans-serif', fontSize: '0.75rem' }}
            >
              <i className="ri-glasses-line" />
              {lang === 'zh' ? '申请DEMO体验' : 'Get Demo'}
            </Link>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes pianoKeyGlow {
          0% { opacity: 0; transform: scaleY(0.85); }
          30% { opacity: 1; transform: scaleY(1); }
          70% { opacity: 1; transform: scaleY(1); }
          100% { opacity: 0.5; transform: scaleY(1); }
        }
      `}</style>
    </nav>
  );
}
